import { Check } from 'lucide-react';
import { MANUAL_BUILDER_STEPS } from './constants';

type ProgressRailProps = {
  currentStep: number;
  skipScreenStep?: boolean;
  /** Highest step id the user may jump to (includes current). */
  maxStepReached?: number;
  /** Navigate when a reachable step is clicked. */
  onStepSelect?: (stepId: number) => void;
  disabled?: boolean;
};

export function ManualProgressRail({
  currentStep,
  skipScreenStep = false,
  maxStepReached,
  onStepSelect,
  disabled = false,
}: ProgressRailProps) {
  const steps = skipScreenStep
    ? MANUAL_BUILDER_STEPS.filter((s) => s.id !== 2)
    : [...MANUAL_BUILDER_STEPS];

  const visualIndex = steps.findIndex((s) => s.id === currentStep);
  const activeIdx = visualIndex >= 0 ? visualIndex : 0;
  const reachableMax = Math.max(maxStepReached ?? currentStep, currentStep);

  return (
    <nav aria-label="Campaign progress" className="w-full">
      <ol className="m-0 flex w-full list-none items-center justify-between overflow-hidden rounded-2xl border border-[#E5EAF0] bg-white px-3 py-2 shadow-[0_2px_12px_-4px_rgba(16,38,73,0.06)] sm:px-5 sm:py-2.5 lg:px-6">
        {steps.map((step, idx) => {
          const done = idx < activeIdx;
          const active = idx === activeIdx;
          const isLast = idx === steps.length - 1;
          const reachable = step.id <= reachableMax;
          const locked = !reachable;
          const interactive =
            Boolean(onStepSelect) && !disabled && !active && reachable;

          const label =
            step.id === 1 ? (
              <>
                <span className="hidden xl:inline">Campaign </span>Basics
              </>
            ) : step.id === 2 ? (
              <>
                <span className="hidden xl:inline">Select </span>Screens
              </>
            ) : step.id === 3 ? (
              <>
                <span className="hidden xl:inline">Schedule & </span>Slots
              </>
            ) : (
              <>
                <span className="hidden xl:inline">Review & </span>Launch
              </>
            );

          const content = (
            <>
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold transition-all duration-200 sm:h-7 sm:w-7 sm:text-xs ${
                  active
                    ? 'bg-[#0D604C] text-white shadow-xs ring-3 ring-[#D1FAE5] sm:ring-4'
                    : done
                      ? 'bg-[#10B981] text-white ring-2 ring-emerald-100'
                      : locked
                        ? 'bg-[#F1F5F9] text-[#94A3B8]'
                        : 'bg-[#E2E8F0] text-[#475569]'
                }`}
                aria-hidden
              >
                {done ? (
                  <Check className="h-3.5 w-3.5 stroke-[2.5]" />
                ) : (
                  idx + 1
                )}
              </span>
              <span
                className={`text-[11px] tracking-tight whitespace-nowrap transition-colors sm:text-xs xl:text-[13px] ${
                  active
                    ? 'font-bold text-[#0D604C]'
                    : done
                      ? 'font-semibold text-[#0F172A]'
                      : locked
                        ? 'font-medium text-[#94A3B8]'
                        : 'font-semibold text-[#334155]'
                }`}
              >
                {label}
              </span>
            </>
          );

          return (
            <li
              key={step.id}
              className="flex min-w-0 flex-1 items-center last:flex-none"
            >
              {interactive ? (
                <button
                  type="button"
                  onClick={() => onStepSelect?.(step.id)}
                  className="flex items-center gap-1.5 rounded-xl px-0.5 py-0.5 text-left transition hover:bg-[#F8FAFC] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--dash-brand)] sm:gap-2"
                  aria-label={`Go to ${step.label}`}
                >
                  {content}
                </button>
              ) : (
                <div
                  className={`flex items-center gap-1.5 sm:gap-2 ${
                    locked ? 'cursor-not-allowed' : ''
                  }`}
                  aria-current={active ? 'step' : undefined}
                  title={
                    locked
                      ? 'Complete earlier steps to unlock'
                      : active
                        ? 'Current step'
                        : undefined
                  }
                >
                  {content}
                </div>
              )}

              {!isLast ? (
                <div
                  className={`mx-1.5 h-[2px] min-w-[8px] flex-1 rounded-full transition-colors duration-300 sm:mx-2 sm:min-w-[12px] xl:mx-3 ${
                    done ? 'bg-[#10B981]' : 'bg-[#E2E8F0]'
                  }`}
                  aria-hidden
                />
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
